/*
  Slickbabe Kit Detail — grid → detail sync
  ─────────────────────────────────────────
  The real Kit Item blocks render (hidden) inside [data-kit-source] — that's
  where the Theme Editor renders and RE-renders them on every add/remove/reorder/
  edit. We never move or mutate those nodes (moving them fights the editor and
  blanks the section). Instead we build the visible UI from CLONES: each item's
  thumbnail is cloned into the grid and its panel into the detail column. A
  MutationObserver rebuilds the clones whenever the editor changes the source.

  Editor selection: clones KEEP their Shopify editor bindings
  (data-shopify-editor-block), which only exist in the editor (empty on the live
  storefront). So clicking a thumbnail, the description, or a spec row on the
  canvas selects the matching Kit Item / Kit Spec Row and opens its settings. We
  drop only `id` from clones to avoid duplicate ids.

  Loaded as an external asset (not inline) so it runs when the editor injects the
  section, and auto-upgrades on full page loads too.
*/
(function () {
  if (customElements.get('sb-kit-detail')) return;

  class SBKitDetail extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-kit-grid]');
      this.panelHost = this.querySelector('[data-kit-panels]');
      this.source = this.querySelector('[data-kit-source]');
      if (!this.grid || !this.panelHost || !this.source) return;

      this.selected = 0;
      this._indexByBlock = {};

      if (!this._wired) {
        this._wired = true;

        this.grid.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-kit-thumb]');
          if (btn && this.grid.contains(btn)) this.select(Number(btn.dataset.kitIndex));
        });
        this.grid.addEventListener('keydown', (e) => this._onKeydown(e));

        // Theme editor: selecting an item or a spec row (tree OR canvas) reveals that item.
        if (window.Shopify && window.Shopify.designMode) {
          this.addEventListener('shopify:block:select', (e) => {
            const el = e.target && e.target.closest && e.target.closest('[data-shopify-editor-block]');
            const id = el ? el.getAttribute('data-shopify-editor-block') : null;
            if (id != null && id in this._indexByBlock) this.select(this._indexByBlock[id]);
          });
        }

        // Rebuild clones whenever the editor changes the real blocks.
        this._observer = new MutationObserver(() => this._scheduleRender());
        this._observer.observe(this.source, { childList: true, subtree: true, characterData: true, attributes: true });
      }

      this._render();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
    }

    _scheduleRender() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._render();
      });
    }

    // Remove ids from a clone (keep data-shopify-editor-block so canvas selection works).
    _clean(node) {
      if (node.removeAttribute) node.removeAttribute('id');
      node.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      return node;
    }

    _render() {
      const items = Array.from(this.source.querySelectorAll('[data-kit-item]'));
      this.thumbs = [];
      this.panels = [];
      this._indexByBlock = {};
      this.grid.textContent = '';
      this.panelHost.textContent = '';

      items.forEach((item, i) => {
        const srcThumb = item.querySelector('[data-kit-thumb]');
        const srcPanel = item.querySelector('[data-kit-panel]');
        if (!srcThumb || !srcPanel) return;

        // Map every editor block id inside this item (the item itself + its spec rows) to its index.
        item.querySelectorAll('[data-shopify-editor-block]').forEach((n) => {
          this._indexByBlock[n.getAttribute('data-shopify-editor-block')] = i;
        });
        const itemBind = srcThumb.getAttribute('data-shopify-editor-block');

        const thumb = this._clean(srcThumb.cloneNode(true));
        const panel = this._clean(srcPanel.cloneNode(true));
        const pid = (this.id || 'sb-kit') + '-panel-' + i;

        thumb.dataset.kitIndex = i;
        thumb.setAttribute('aria-controls', pid);
        panel.id = pid;
        panel.hidden = true;
        // Let clicking anywhere in the panel (e.g. the description) select this Kit Item.
        if (itemBind) panel.setAttribute('data-shopify-editor-block', itemBind);

        this.grid.appendChild(thumb);
        this.panelHost.appendChild(panel);
        this.thumbs.push(thumb);
        this.panels.push(panel);
      });

      if (!this.thumbs.length) {
        this.classList.remove('is-ready');
        return;
      }
      if (this.selected >= this.thumbs.length) this.selected = this.thumbs.length - 1;
      this.classList.add('is-ready');
      this.select(this.selected);
    }

    select(index) {
      if (index < 0 || index >= this.thumbs.length) return;
      this.selected = index;
      this.thumbs.forEach((thumb, i) => {
        const on = i === index;
        thumb.classList.toggle('is-selected', on);
        thumb.setAttribute('aria-pressed', on ? 'true' : 'false');
        thumb.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
    }

    _onKeydown(e) {
      const last = this.thumbs.length - 1;
      let next = this.selected;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown': next = this.selected >= last ? 0 : this.selected + 1; break;
        case 'ArrowLeft':
        case 'ArrowUp': next = this.selected <= 0 ? last : this.selected - 1; break;
        case 'Home': next = 0; break;
        case 'End': next = last; break;
        default: return;
      }
      e.preventDefault();
      this.select(next);
      this.thumbs[next].focus();
    }
  }

  customElements.define('sb-kit-detail', SBKitDetail);
})();
